import type { Locale } from "@/i18n";

const tag = (locale: Locale) => (locale === "zh" ? "zh-CN" : "en-US");

export function number(locale: Locale, value: number): string {
	return new Intl.NumberFormat(tag(locale)).format(value);
}

export function percent(locale: Locale, fraction: number, digits = 0): string {
	return new Intl.NumberFormat(tag(locale), { style: "percent", maximumFractionDigits: digits }).format(fraction);
}

/** USD micros to a readable amount. */
export function usd(locale: Locale, micros: number): string {
	return new Intl.NumberFormat(tag(locale), { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(
		micros / 1_000_000,
	);
}

export function cny(locale: Locale, micros: number): string {
	return new Intl.NumberFormat(tag(locale), { style: "currency", currency: "CNY", maximumFractionDigits: 2 }).format(
		micros / 1_000_000,
	);
}

export function date(locale: Locale, iso: string | null | undefined): string {
	if (!iso) return "—";
	const value = new Date(iso);
	if (Number.isNaN(value.getTime())) return "—";
	return new Intl.DateTimeFormat(tag(locale), { year: "numeric", month: "short", day: "numeric" }).format(value);
}

export function dateTime(locale: Locale, iso: string | null | undefined): string {
	if (!iso) return "—";
	const value = new Date(iso);
	if (Number.isNaN(value.getTime())) return "—";
	return new Intl.DateTimeFormat(tag(locale), {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		// Operators sit in more than one zone and the server's rows are UTC; a
		// time with no zone on it is a time somebody will misread by hours.
		timeZoneName: "short",
	}).format(value);
}

/**
 * The last instant of a calendar day in the operator's own zone, as an ISO
 * instant. `new Date("2026-03-01")` is UTC midnight, which west of Greenwich
 * displays as the last day of February — an expiry set for the first would
 * show, and lapse, on the day before.
 */
export function endOfDay(day: string): string {
	return new Date(`${day}T23:59:59.999`).toISOString();
}

export function relative(locale: Locale, iso: string | null | undefined): string | null {
	if (!iso) return null;
	const value = new Date(iso).getTime();
	if (Number.isNaN(value)) return null;
	const seconds = Math.round((value - Date.now()) / 1000);
	const rtf = new Intl.RelativeTimeFormat(tag(locale), { numeric: "auto" });
	const units: [Intl.RelativeTimeFormatUnit, number][] = [
		["year", 31_536_000],
		["month", 2_592_000],
		["week", 604_800],
		["day", 86_400],
		["hour", 3_600],
		["minute", 60],
	];
	for (const [unit, size] of units) {
		if (Math.abs(seconds) >= size) return rtf.format(Math.round(seconds / size), unit);
	}
	return rtf.format(seconds, "second");
}

/** A signed delta, always with its sign. */
export function signed(locale: Locale, value: number): string {
	return new Intl.NumberFormat(tag(locale), { signDisplay: "exceptZero" }).format(value);
}
